/* Company Confidential, Copyright (c) 2021 Signant Health, Ltd. All Rights Reserved. */

import { Button, TextField } from '@material-ui/core';
import React from 'react';
import { useIntl } from 'react-intl';
import { Image as ImageIcon, Clear as ClearIcon } from '@material-ui/icons';
import { StyledDropzone } from './StyledDropzone';
import config from '../config';
import { FORM_FIELDS, MAX_FILE_SIZE, REQUIRED_FIELDS } from '../consts';
import Info from './Info';
import FooterButtons from './FooterButtons';
import Dropdown from './Dropdown';
import { LightTooltip } from './Tooltip';

export default function RenderForm({
  formType,
  values,
  fileValidator,
  onChange,
  onDropRejected,
  validate,
  errors,
  files,
  onFileChange,
  onFileDelete,
  resetState,
  sites,
  roles,
  message
}) {
  const { formatMessage } = useIntl();
  const { maxAdditionalContacts } = config;

  let dropDowns = ['priority', 'patient', 'site', 'userChangeType'];
  if (values.userChangeType === formatMessage({ id: 'form.singleUser' })) {
    dropDowns = ['priority', 'patient', 'userRole', 'userType', 'site', 'userChangeType'];
  }
  const isFieldRequired = (field) =>
    REQUIRED_FIELDS.all.includes(field) || REQUIRED_FIELDS.UCF.includes(field);

  return (
    <div className="page">
      <h1 className="form-title">{`${formatMessage({ id: `response.${formType}` })} (${formType})`}</h1>
      {message !== formatMessage({ id: 'form.message' }) && message !== '' && <Info text={message} />}
      <div className="row">
        <div className="column form-inputs">
          {FORM_FIELDS[formType].map((field) => {
            if (dropDowns.includes(field)) {
              return (
                <Dropdown
                  key={field}
                  values={values}
                  errors={errors}
                  field={field}
                  sites={sites}
                  onChange={onChange}
                  roles={roles}
                />
              );
            }
            if (['additional', 'subject'].includes(field)) {
              return (
                <div className="input-container" key={field}>
                  <LightTooltip
                    title={
                      <span style={{ fontSize: '12px' }}>
                        {formatMessage({ id: `general.${field}` }, { maxAdditionalContacts })}
                      </span>
                    }
                    placement="right-end"
                  >
                    <TextField
                      label={formatMessage({ id: `form.${field}` })}
                      error={!!errors[field]}
                      helperText={errors[field]}
                      id={field}
                      variant="outlined"
                      type="text"
                      required={isFieldRequired(field)}
                      value={values[field] || ''}
                      name={field}
                      onChange={onChange}
                      margin="normal"
                      placeholder={`${formatMessage({ id: 'form.enter' })} ${formatMessage({
                        id: `form.${field}`
                      })}`}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      multiline={field === 'additional'}
                    />
                  </LightTooltip>
                </div>
              );
            }
            if (field === 'reason') {
              return (
                <div className="input-container" key={field}>
                  <LightTooltip
                    title={
                      <span style={{ fontSize: '12px' }}>{formatMessage({ id: `general.${field}` })}</span>
                    }
                    placement="right-end"
                  >
                    <TextField
                      style={{ resize: 'none' }}
                      label={formatMessage({ id: `form.${field}` })}
                      error={!!errors[field]}
                      id={field}
                      variant="outlined"
                      rows={3}
                      maxRows={5}
                      type="text"
                      required={isFieldRequired(field)}
                      value={values[field] || ''}
                      inputProps={{ maxLength: 1000 }}
                      name={field}
                      multiline
                      onChange={onChange}
                      margin="normal"
                      placeholder={`${formatMessage({ id: 'form.enter' })} ${formatMessage({
                        id: `form.${field}`
                      })}`}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </LightTooltip>
                </div>
              );
            }
            if (field === 'change') {
              return (
                <div className="input-container" key={field}>
                  <LightTooltip
                    title={
                      <span style={{ fontSize: '12px' }}>{formatMessage({ id: `general.${field}` })}</span>
                    }
                    placement="right-end"
                  >
                    <TextField
                      style={{ resize: 'none' }}
                      label={formatMessage({ id: `form.${field}` })}
                      id={field}
                      variant="outlined"
                      rows={3}
                      maxRows={5}
                      error={!!errors[field]}
                      required={isFieldRequired(field)}
                      value={values[field] || ''}
                      inputProps={{ maxLength: 1000 }}
                      name={field}
                      multiline
                      onChange={onChange}
                      margin="normal"
                      placeholder={`${formatMessage({ id: 'form.enter' })} ${formatMessage({
                        id: `form.${field}`
                      })}`}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </LightTooltip>
                </div>
              );
            }
            return null;
          })}
        </div>

        <div className="upload-container" key="upload">
          {FORM_FIELDS[formType].includes('upload') && (
            <LightTooltip
              title={
                <span style={{ fontSize: '12px' }}>{formatMessage({ id: 'general.fileUpload' })}</span>
              }
              placement="top"
            >
              <div>
                <StyledDropzone
                  validator={fileValidator}
                  onDropAccepted={onFileChange}
                  onDropRejected={onDropRejected}
                  maxFileSize={MAX_FILE_SIZE}
                />
                <div className="file-list">
                  {files.length === 0 ? (
                    <div className="file-list" />
                  ) : (
                    <div className="column">
                      {files.map((f, i) => (
                        <div className="row" data-testid={f.name} key={f.path || f.name || i}>
                          <ImageIcon />
                          <p>{f.name}</p>
                          <Button onClick={() => onFileDelete(i)} className="btn_delete">
                            <ClearIcon className="icon_fontsize" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </LightTooltip>
          )}
        </div>
      </div>
      <FooterButtons cancel={resetState} next={validate} />
    </div>
  );
}
